import React, { useEffect, useState } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";
import { updatePromocode } from "../../services/VoucherService";
import { ALL_CURRENCIES } from "../../constants/currencies";
import "./AdminModalForm.css";

// Admin modal for editing an existing promocode
const AdminEditPromocodeModal = ({ isOpen, onClose, promocode, onUpdated }) => {
  // Local form state for promocode fields
  const [formData, setFormData] = useState({
    code: "",
    type: "topup_amount",
    amount: "",
    currency: "",
    date_end: "",
    activations_left: "",
    user_activation_count: "",
  });

  // UI state for loading and feedback
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Populate form when modal is opened or promocode changes
  useEffect(() => {
    if (isOpen && promocode) {
      setFormData({
        code: promocode.code || "",
        type: promocode.type || "topup_amount",
        amount: promocode.amount ?? "",
        currency: ALL_CURRENCIES.some(({ id }) => id === promocode.currency) ? promocode.currency : "EUR",
        date_end: promocode.date_end ? new Date(promocode.date_end).toISOString().slice(0, 16) : "",
        activations_left: promocode.activations_left ?? "",
        user_activation_count: promocode.user_activation_count ?? "",
      });
      setError("");
      setSuccess("");
    }
  }, [isOpen, promocode]);

  // Do not render if modal is closed or no promocode provided
  if (!isOpen || !promocode) return null;

  // Handle input changes, including numeric conversion
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
    setError("");
  };

  // Build payload for update request
  const buildPayload = () => ({
    type: formData.type,
    amount: Number(formData.amount),
    currency: formData.currency,
    date_end: formData.date_end ? new Date(formData.date_end).toISOString() : null,
    activations_left: Number(formData.activations_left),
    user_activation_count: Number(formData.user_activation_count),
  });

  // Handle form submit and call update API
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSaving(true);

      const payload = buildPayload();
      await updatePromocode(promocode.code, payload);

      setSuccess("Promocode updated successfully.");
      setTimeout(() => {
        onUpdated?.();
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.message || "Failed to update promocode");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={`Edit Promocode: ${promocode.code}`}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </MainButton>
        </>
      }
    >
      {/* Promocode edit form */}
      <form onSubmit={handleSubmit} className="admin-modal-form">
        {/* Read-only promocode code */}
        <label>
          Code
          <MainInput type="text" name="code" value={formData.code} disabled />
        </label>

        {/* Promocode type selector */}
        <label>
          Type
          <div className="admin-modal-select-wrapper">
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              <option value="topup_amount">Top-up amount</option>
              <option value="sell_discount">Sell discount</option>
            </select>
          </div>
        </label>

        {/* Amount field */}
        <label>
          Amount
          <MainInput
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Currency field */}
        <label>
          Currency
          <div className="admin-modal-select-wrapper">
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              required
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              {ALL_CURRENCIES.map(({ id, name }) => (
                <option key={id} value={id}>
                  {id} — {name}
                </option>
              ))}
            </select>
          </div>
        </label>

        {/* Expiration date field */}
        <label>
          Expires at (UTC)
          <MainInput
            type="datetime-local"
            name="date_end"
            value={formData.date_end}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Activations left field */}
        <label>
          Activations left
          <MainInput
            type="number"
            name="activations_left"
            value={formData.activations_left}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Max per user field */}
        <label>
          Max per user
          <MainInput
            type="number"
            name="user_activation_count"
            value={formData.user_activation_count}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Error message */}
        {error && (
          <div className="admin-modal-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="admin-modal-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AdminEditPromocodeModal;
